import { z } from 'zod';
import { ActedForAction, ActForRole } from './acting-for-base.js';
import { AwardView, OperationalAwardTypeCode, StageId } from './awards.js';
import { Cents, PlainDate } from './common.js';

const Count = z.number().int().nonnegative();
const Ref = z.object({ id: z.string(), name: z.string() });
const PersonRef = Ref.extend({ initials: z.string() });
const AwardRef = z.object({ id: z.uuid(), displayId: z.string() });

/** The Stock Admin's lists (SA1, HOME2). */
export const QueueViewId = z.enum([
  'queue',
  'mine',
  /** PAA people whose tax from ADP is still to enter (APP-101); its own page lists them. */
  'tax',
  'in_progress',
  'past_deadline',
  'letters',
  'earning_statements',
  'shareworks',
]);
export type QueueViewId = z.infer<typeof QueueViewId>;

/** "All sectors · awards by stage" (SA1): plain step names, never S-codes. */
export const QueueStep = z.enum([
  'waiting_for_claim',
  'checking',
  'letters',
  'employee_choice',
  'past_deadline',
  'with_payroll',
  /** Paid: the Stock Admin checks the earning statement (SA6). */
  'statement_check',
  'shareworks',
]);
export type QueueStep = z.infer<typeof QueueStep>;

/**
 * Who an award at Approval waits on (QA-013), the way SL2 says it: the Sector Lead (a delegate's
 * entry), any one Executive (an Executive decision, or over budget), or the Executives outside the
 * app (Executive Discretionary).
 */
export const QueueApproval = z.object({
  on: z.enum(['sector_lead', 'named_executive', 'executive', 'executives_outside_app']),
  /** The Sector Lead a delegate's entry waits on. */
  person: Ref.nullable(),
  /** Waiting because it goes over its pool's budget. */
  overBudget: z.boolean(),
});
export type QueueApproval = z.infer<typeof QueueApproval>;

/**
 * What an award at the check still needs before it can be marked checked from the list
 * (APP-105): lines, coding, its proof (EDB) or write-up (PAA, TAA), a share price that still
 * matches today's, or someone else holds it. The tax from ADP never holds it (APP-101 §6).
 */
// `option_value`: a PAA since APP-151 counts its options in the CFO's value of one option, which
// changed after it was sent.
export const CheckNeed = z.enum([
  'lines',
  'coding',
  'proof',
  'write_up',
  'fmv',
  'option_value',
  'claimed',
]);
export type CheckNeed = z.infer<typeof CheckNeed>;

/** One person on an award at the check, as the queue shows them (APP-109). */
export const QueueCheckPerson = z.object({
  person: Ref,
  cashCents: Cents,
  options: Count,
  pool: Ref,
  costType: z.enum(['OH', 'GA']),
  /** Null until the line is coded. The project is text: 15125.110 keeps its zero. */
  coding: z
    .object({ account: z.string(), org: z.string(), project: z.string().nullable() })
    .nullable(),
});
export type QueueCheckPerson = z.infer<typeof QueueCheckPerson>;

/**
 * An award at the check step: whether it can be marked checked from the list, else what it needs,
 * and what marking it checked confirms (APP-109): each person, their amounts, pool and charge
 * codes, its proof or write-up and the share price its options use, all of the version the row
 * carries, so what the list shows is what Mark checked sends.
 */
export const QueueCheck = z.object({
  ready: z.boolean(),
  needs: z.array(CheckNeed),
  /** In entry order. */
  people: z.array(QueueCheckPerson),
  proof: z.object({ needed: z.enum(['write_up', 'proof']).nullable(), present: z.boolean() }),
  /** The share price the options use; null when the award pays cash only. */
  fmvCents: Cents.nullable(),
});
export type QueueCheck = z.infer<typeof QueueCheck>;

export const QueueRow = z.object({
  award: AwardRef,
  /** APP-244: a Spot award joins the queue at Letters only, its letter to review. */
  type: OperationalAwardTypeCode,
  /**
   * What the award is: the person (one-person award), else its title, its program, or how many
   * people ("3 people"). Never how it pays; the cash and options columns say that.
   */
  subject: z.string(),
  /** The pool or pools that pay: "Maritime Airborne", "PAA/TAA pool and Maritime HydroVision". */
  pool: z.string(),
  people: Count,
  cashCents: Cents,
  /** Null for cash-only awards. */
  options: Count.nullable(),
  submittedBy: Ref,
  /** When it was submitted (APP-127): the Submitted column, so the team sees how old each is. */
  submittedAt: z.iso.datetime({ offset: true }),
  /** When it reached the Stock Admin team, or was sent for the earlier stages. */
  since: z.iso.datetime({ offset: true }),
  stage: StageId.nullable(),
  step: QueueStep.nullable(),
  /** At Approval: who it waits on. Null at every other stage. */
  approval: QueueApproval.nullable(),
  claimedBy: PersonRef.nullable(),
  /** The award's version, sent back with Claim. */
  version: z.number().int(),
  /** APP-148: what a Stock Admin did for someone on this award, for the Approval column. */
  doneForOthers: z.array(z.object({ actor: Ref, for: Ref, action: ActedForAction })).optional(),
  /** APP-148 D17: held by "Not what you asked?". */
  held: z.boolean().optional(),
  /**
   * Whose review a held award waits on (D17): an Executive's, or the Sector Lead's, by whom the
   * questioned action was done for. Null when it isn't held. Optional: older servers omit it.
   */
  heldFor: ActForRole.nullable().optional(),
  /** At the check step (waiting for a claim, or being checked): ready from the list? Else null. */
  check: QueueCheck.nullable(),
  /**
   * APP-130: at the letters step, how many letters have gone out of how many, and whether a
   * letter some of them waited on is ready now (D16). Null at every other step.
   */
  letters: z
    .object({
      released: Count,
      total: Count,
      templateArrived: z.boolean(),
      /**
       * APP-244: the automatic run of its letters, when one speaks for them: being made, or not
       * made with the codes of why (the Letters page says what to do). Null when there's nothing
       * to say. Optional: older servers omit it.
       */
      automatic: z
        .object({ status: z.enum(['waiting', 'not_made']), codes: z.array(z.string()) })
        .nullable()
        .optional(),
    })
    .nullable()
    .optional(),
  /**
   * APP-130 D18: before the letters go out, how many people's letters can't be made yet (no letter
   * for their design, a sample still to review, or nobody to sign). Worked out live, so it clears
   * when the letter arrives; 0 when there's no gap. Optional: older servers omit it.
   */
  letterGap: Count.optional(),
});
export type QueueRow = z.infer<typeof QueueRow>;

export const StockAdminQueue = z.object({
  view: QueueViewId,
  /**
   * Whether the reader works the queue: the Stock Admin team. A System Admin sees it (APP-134)
   * without Claim, ticking or Mark checked, and without a My queue of their own.
   */
  processes: z.boolean(),
  views: z.array(z.object({ id: QueueViewId, count: Count })),
  steps: z.array(z.object({ id: QueueStep, count: Count })),
  /** Oldest first. */
  rows: z.array(QueueRow),
  mine: z.object({ checks: Count, letters: Count }),
  /**
   * APP-148: how many of the listed rows the reader did something on for an Executive or a Sector
   * Lead ("You did 4 of these for someone else"); each row's `doneForOthers` says what and for whom.
   * Optional: older servers omit it.
   */
  doneForOthersByYou: Count.optional(),
  /**
   * Grant rows left to enter in Shareworks (each person's options and shares, a row each), by the
   * worklist's own rule (SA5). The heroes and the Shareworks
   * view count this, so they say the same number as the worklist (APP-067 §13).
   */
  shareworksRows: Count,
  /** Legacy response field; APP-121 always returns an empty list. */
  hiddenOwn: z.array(z.object({ award: AwardRef, with: Ref.nullable() })),
  /** The rest of the Stock Admin team. */
  teammates: z.array(PersonRef),
  /**
   * How a new request in the queue reaches the reader by email now (APP-092): one email per
   * request, in their daily digest, or not by email. The empty queue says only what's true.
   */
  queueEmail: z.enum(['per_request', 'digest', 'off']).optional(),
});
export type StockAdminQueue = z.infer<typeof StockAdminQueue>;

/**
 * SA1's "Today": what happened on the queue today, newest first: claims, approvals, checks,
 * letters, Payroll's payments and statements, Shareworks entries. Never an award the reader is on.
 */
export const QueueActivityKind = z.enum([
  'claim',
  'approval',
  'check',
  'letters',
  'payroll',
  'statement',
  'shareworks',
  'other',
]);
export type QueueActivityKind = z.infer<typeof QueueActivityKind>;

export const QueueActivityItem = z.object({
  id: z.uuid(),
  at: z.iso.datetime({ offset: true }),
  kind: QueueActivityKind,
  /** "Tyler Brandt claimed TAA-2026-0004". */
  text: z.string(),
  /** "Team Achievement Award · Sea trials crew". */
  detail: z.string(),
  award: AwardRef,
});
export type QueueActivityItem = z.infer<typeof QueueActivityItem>;

export const QueueToday = z.object({
  /** The organization's day these are from. */
  asOf: PlainDate,
  items: z.array(QueueActivityItem),
  /** More happened today than is listed. */
  more: z.boolean(),
});
export type QueueToday = z.infer<typeof QueueToday>;

export const ClaimAward = z.object({ version: z.number().int().positive() }).strict();
export type ClaimAward = z.infer<typeof ClaimAward>;

/** APP-271: take a teammate's claim over at Letters. The reason is kept in the award's history. */
export const TakeOverClaim = z
  .object({ version: z.number().int().positive(), reason: z.string().trim().min(1).max(2000) })
  .strict();
export type TakeOverClaim = z.infer<typeof TakeOverClaim>;

/** Hand a claim back, or (System Admin) release a stuck one, which needs a reason. */
export const ReleaseClaim = z
  .object({ reason: z.string().trim().min(1).max(2000).optional() })
  .strict();
export type ReleaseClaim = z.infer<typeof ReleaseClaim>;

export const ElectionChoiceId = z.enum(['a', 'b', 'c', 'd']);
export const TaxEstimates = z.object({ a: Cents, b: Cents, c: Cents, d: Cents }).strict();
export type TaxEstimates = z.infer<typeof TaxEstimates>;

/** One person's line at the check (SA2). */
export const CheckLine = z.object({
  lineId: z.uuid(),
  person: z.object({
    id: z.uuid(),
    name: z.string(),
    eeNumber: z.string().nullable(),
    homeOrg: z.string().nullable(),
    manager: z.string().nullable(),
    /** Who last set their home org or OH/G&A in MyRewards, and when; null if never changed. */
    codingChanged: z.object({ at: z.iso.datetime({ offset: true }), by: z.string() }).nullable(),
  }),
  cashCents: Cents,
  options: z.number().int(),
  totalValueCents: Cents.nullable(),
  pool: Ref,
  costType: z.enum(['OH', 'GA']),
  /** OH or G&A switched at the check, not from the directory. */
  costTypeSwitched: z.boolean(),
  coding: z
    .object({ account: z.string(), project: z.string().nullable(), org: z.string() })
    .nullable(),
  /** PAA with cash: the estimated tax per choice, as entered from ADP; null until entered. */
  taxEstimates: TaxEstimates.nullable(),
  /** Who the letter goes to, when it's already chosen; otherwise it's settled at the letters step. */
  letterRecipient: Ref.nullable(),
});

export const CheckItem = z.object({
  id: z.enum(['lines', 'fmv', 'coding', 'proof', 'tax']),
  status: z.enum(['done', 'open', 'not_needed']),
});

export const AwardCheck = z.object({
  award: AwardView,
  claimedBy: PersonRef.nullable(),
  /** When the claim was taken (SA2's hero: "Claimed by you · 9:41 AM"); null with no claim. */
  claimedAt: z.iso.datetime({ offset: true }).nullable(),
  /** The signed-in Stock Admin holds the claim and can make changes. */
  yours: z.boolean(),
  lines: z.array(CheckLine),
  fmv: z.object({
    used: z.object({ cents: Cents, effectiveFrom: PlainDate.nullable() }),
    /** The FMV in effect on the award date today; differs when FMV changed after sending. */
    current: z.object({ cents: Cents, effectiveFrom: PlainDate }).nullable(),
    matches: z.boolean(),
  }),
  /**
   * APP-151: the CFO's value of one option a PAA since APP-151 counts its options in, as `fmv`.
   * Null for every other award, a PAA sent before APP-151 included.
   */
  optionValue: z
    .object({
      used: z.object({ cents: Cents, effectiveFrom: PlainDate.nullable() }),
      /** The value in effect on the award date today; null when none is set for that date. */
      current: z.object({ cents: Cents, effectiveFrom: PlainDate }).nullable(),
      matches: z.boolean(),
    })
    .nullable(),
  /** The file the award needs before its letter: a PAA/TAA write-up or Executive Discretionary proof. */
  proof: z.object({
    needed: z.enum(['write_up', 'proof']).nullable(),
    present: z.boolean(),
  }),
  checklist: z.array(CheckItem),
  canComplete: z.boolean(),
  /** The check is done: the award is at letters or later. */
  completed: z.boolean(),
  /** Who approved it and when, when it needed an approval. */
  approved: z.object({ by: Ref.nullable(), at: z.iso.datetime({ offset: true }) }).nullable(),
  /** What the signed-in person may do from here, as the server decides. */
  actions: z.object({
    claim: z.boolean(),
    /** Hand your own claim back. */
    handBack: z.boolean(),
    /** A System Admin releases someone else's claim, with a reason. */
    releaseOthers: z.boolean(),
    /** APP-271: a teammate holds it at Letters, and the reader may take it over. */
    takeOver: z.boolean(),
    sendBack: z.boolean(),
  }),
});
export type AwardCheck = z.infer<typeof AwardCheck>;

export const SetLineCostType = z
  .object({
    version: z.number().int().positive(),
    lineId: z.uuid(),
    costType: z.enum(['OH', 'GA']),
    reason: z.string().trim().min(1).max(2000),
  })
  .strict();
export type SetLineCostType = z.infer<typeof SetLineCostType>;

/** Take the values again as they are today (FMV, split, codes), with a reason. */
export const Resnapshot = z
  .object({ version: z.number().int().positive(), reason: z.string().trim().min(1).max(2000) })
  .strict();
export type Resnapshot = z.infer<typeof Resnapshot>;

/** Mark checked (APP-105): the press is the confirmation; the server checks what's still open. */
export const CompleteCheck = z.object({ version: z.number().int().positive() }).strict();
export type CompleteCheck = z.infer<typeof CompleteCheck>;

/** Mark several checked from the queue (APP-105): each award with the version the list showed. */
export const CompleteChecks = z
  .object({
    items: z
      .array(z.object({ id: z.uuid(), version: z.number().int().positive() }).strict())
      .min(1)
      .max(100),
  })
  .strict();
export type CompleteChecks = z.infer<typeof CompleteChecks>;

/**
 * Which were marked checked, and which stayed in the queue and why: the award changed since the
 * list showed it, someone else holds it, it isn't ready (with what it needs), or it isn't one the
 * reader may check (their own, out of scope, Preview).
 */
export const CompletedChecks = z.object({
  checked: z.array(AwardRef),
  left: z.array(
    z.object({
      award: AwardRef,
      reason: z.enum(['changed', 'claimed', 'not_ready', 'not_found']),
      needs: z.array(CheckNeed),
    }),
  ),
});
export type CompletedChecks = z.infer<typeof CompletedChecks>;

/** SA2 "Send back with a note": the award goes back to its requester as a Draft (APP-042). */
export const ReturnCheck = z
  .object({
    version: z.number().int().positive(),
    note: z.string().trim().min(1).max(2000),
  })
  .strict();
export type ReturnCheck = z.infer<typeof ReturnCheck>;
